import Axios from "axios";
import { useEffect, useState } from "react";
import { Box, IconButton } from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import { Dialog, DialogTitle, DialogContent, TextField, DialogActions, Button } from "@mui/material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";

const Expenses = () => {

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editExpense, setEditExpense] = useState(null);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [newExpense, setNewExpense] = useState({
    title: "",
    amount: "",
    expense_date: "",
    description: ""
  });

  const [mappedData, setMappedData] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch expenses
  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await Axios.get("https://store.petplanet.rw/api/expenses");
        setMappedData(res.data || []);
      } catch (error) {
        console.error("Error fetching expenses", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const refreshData = async () => {
    const res = await Axios.get("https://store.petplanet.rw/api/expenses");
    setMappedData(res.data || []);
  };

  // Add Expense
  const handleAddExpense = async () => {

    if (!newExpense.title || !newExpense.amount) {
      alert("Please enter expense title and amount");
      return;
    }

    try {

      await Axios.post("https://store.petplanet.rw/api/expenses", newExpense);

      alert("Expense added successfully!");

      setIsAddModalOpen(false);

      setNewExpense({
        title: "",
        amount: "",
        expense_date: "",
        description: ""
      });

      refreshData();

    } catch (error) {
      console.error("Error adding expense:", error);
    }

  };

  // Update Expense
  const handleSave = async () => {

    try {

      await Axios.put(
        `https://store.petplanet.rw/expenses/${editExpense.id}`,
        editExpense
      );

      alert("Expense updated successfully!");

      setIsEditModalOpen(false);

      refreshData();

    } catch (error) {
      console.error("Error updating expense:", error);
    }

  };

  // Delete Expense
  const handleDelete = async (id) => {

    const confirmDelete = window.confirm("Are you sure you want to delete this expense?");
    if (!confirmDelete) return;

    try {

      await Axios.delete(`https://store.petplanet.rw/api/expenses/${id}`);

      alert("Expense deleted successfully!");

      refreshData();

    } catch (error) {
      console.error("Delete failed:", error);
    }

  };

  const columns = [

    {
      field: "title",
      headerName: "Expense Title",
      flex: 1
    },

    {
      field: "amount",
      headerName: "Amount",
      flex: 1,
      renderCell: (params) => {

        const value = parseFloat(params.value);

        return (
          <Box>
            {value.toLocaleString()} RWF
          </Box>
        );
      }
    },

    {
      field: "expense_date",
      headerName: "Date",
      flex: 1
    },

    {
      field: "description",
      headerName: "Description",
      flex: 2
    },

    {
      field: "actions",
      headerName: "Actions",
      renderCell: (params) => (

        <Box>

          <IconButton
            color="secondary"
            onClick={() => {
              setEditExpense(params.row);
              setIsEditModalOpen(true);
            }}
          >
            <EditOutlinedIcon />
          </IconButton>

          <IconButton
            style={{ color: "#FF0000" }}
            onClick={() => handleDelete(params.row.id)}
          >
            <DeleteOutlinedIcon />
          </IconButton>

        </Box>
      )
    }

  ];

  return (

    <>
      <Box m="20px" mt="80px">

        <Box display="flex" justifyContent="space-between" alignItems="center">

          <Header title="Expenses" subtitle="Manage your business expenses" />

          <IconButton
            onClick={() => setIsAddModalOpen(true)}
            sx={{
            fontSize: "13px",
            backgroundColor: tokens.redAccent[400],
            border: "none",
            color: tokens.grey[100],
            padding: "14px",
            borderRadius: "10px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "5px",
            marginTop: "-13px",
            transition: "all 0.3s ease",
            "&:hover": {
              outline: `2px solid ${tokens.redAccent[400]}`,
              outlineOffset: "1px",
              backgroundColor: tokens.redAccent[200],
            },
            "&:active": {
              outline: `2px solid ${tokens.redAccent[500]}`,
              transform: "scale(0.97)",
            },
            }}
          >
            Add Expense <AddOutlinedIcon fontSize="15px" />
          </IconButton>

        </Box>

        <Box 
          m="20px 0 0 0"
          height="75vh"
          sx={{
            "& .MuiDataGrid-root": {
              border: "none",
            },
            "& .MuiDataGrid-cell": {
              borderBottom: "none",
            },
            "& .name-column--cell": {
              color: tokens.grey[800],
            },
            "& .MuiDataGrid-virtualScroller": {
              backgroundColor: tokens.grey[100],
            },
            "& .MuiCheckbox-root": {
              border: "none",
              color: `${tokens.redAccent[400]} !important`,
            },
            "& .MuiDataGrid-columnHeader.action-column--header": {
              background: "none",
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              color: `${tokens.grey[900]} !important`,
              fontSize: "12px",
            },
            "& .MuiDataGrid-columnSeparator--resizable": {
              color: `${tokens.blueAccent[100]} !important`,
            }
          }}
        >

          <DataGrid
            checkboxSelection
            rows={mappedData}
            columns={columns}
            loading={loading}
            slots={{ toolbar: GridToolbar }}
          />

        </Box>

      </Box>

      {/* ADD MODAL */}

      <Dialog open={isAddModalOpen} onClose={() => setIsAddModalOpen(false)}>

        <DialogTitle fontSize="20px" backgroundColor={tokens.redAccent[200]} color={tokens.blueAccent[400]}>
            Add Expense
        </DialogTitle>

        <DialogContent>

          <TextField
            margin="normal"
            label="Expense Title"
            fullWidth
            value={newExpense.title}
            onChange={(e) =>
              setNewExpense({ ...newExpense, title: e.target.value })
            }
          />
          <Box display="flex" gap="10px">
          <TextField
            margin="normal"
            label="Amount"
            type="number"
            fullWidth
            value={newExpense.amount}
            onChange={(e) =>
              setNewExpense({ ...newExpense, amount: e.target.value })
            }
          />

          <TextField
            margin="normal"
            label="Date"
            type="date"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={newExpense.expense_date}
            onChange={(e) =>
              setNewExpense({ ...newExpense, expense_date: e.target.value })
            }
          />
          </Box>
          <TextField
            margin="normal"
            label="Description"
            fullWidth
            multiline
            rows={3}
            value={newExpense.description}
            onChange={(e) =>
              setNewExpense({ ...newExpense, description: e.target.value })
            }
          />

        </DialogContent>

        <DialogActions style={{ background: tokens.redAccent[200] }}>
           <Button style={{ color: tokens.blueAccent[400] }}
                  onClick={() => setIsAddModalOpen(false)}  
           >
            Cancel
           </Button>
          <Button style={{ color: tokens.blueAccent[400] }}
            onClick={handleAddExpense}>
            Save
          </Button>
        </DialogActions>

      </Dialog>
      {/* EDIT MODAL */}
        <Dialog sx={{
          "& .MuiDialog-paper": {
            width:"800px",
            position: "absolute",
            top: "0",
            left: "60%",
            transform: "translateX(-50%)",
          }
        }} open={isEditModalOpen} onClose={() => setIsEditModalOpen(false)}>
        
          <DialogTitle
            style={{
              background: tokens.redAccent[200],
              color: tokens.blueAccent[400],
              fontSize: "17px"
            }}
          >
            Edit Expense
          </DialogTitle>
        
          <DialogContent style={{ background: tokens.grey[100], padding:"20px 10px"}}>
        
            <Box style={{display:"flex", justifyContent:"space-between", marginTop:"10px", gap:"20px"}}>
        
              <TextField
                margin="normal"
                label="Expense Title"
                fullWidth
                value={editExpense?.title || ""}
                onChange={(e) =>
                  setEditExpense({ ...editExpense, title: e.target.value })
                }
              />
        
              <TextField
                margin="normal"
                label="Amount"
                type="number"
                fullWidth
                value={editExpense?.amount || ""}
                onChange={(e) =>
                  setEditExpense({ ...editExpense, amount: e.target.value })
                }
              />
        
              <TextField
                margin="normal"
                label="Expense Date"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={editExpense?.expense_date || ""}
                onChange={(e) =>
                  setEditExpense({ ...editExpense, expense_date: e.target.value })
                }
              />
        
            </Box>
        
            <Box style={{display:"flex", justifyContent:"space-between", marginTop:"10px", gap:"20px"}}>
        
              <TextField
                margin="normal"
                label="Description"
                fullWidth
                multiline
                rows={3}
                value={editExpense?.description || ""}
                onChange={(e) =>
                  setEditExpense({ ...editExpense, description: e.target.value })
                }
              />
        
            </Box>
        
          </DialogContent>
        
          <DialogActions style={{background:tokens.redAccent[200]}}>
        
            <Button
              style={{color: tokens.blueAccent[400]}}
              onClick={() => setIsEditModalOpen(false)}
            >
              Cancel
            </Button>
        
            <Button
              style={{color: tokens.blueAccent[400]}}
              onClick={handleSave}
            >
              Save
            </Button>
        
          </DialogActions>
        </Dialog>
        
    </>
  );
};

export default Expenses;